import { useReactFlow, type XYPosition } from '@xyflow/react'
import { useCallback, useMemo } from 'react'
import { DraggableNode } from './draggable_node'
import { Text } from '@/components/base/text'
import type { ItemTypeDetailFragment } from '@/gql/graphql'

let id = 0
const getId = () => `dndnode_${id++}`

interface Props {
  itemTypeId: string
  defaultData: {
    connectorFamilies: ItemTypeDetailFragment['connectorFamilies']
  }
}

export const Palette = ({ itemTypeId, defaultData }: Props) => {
  const { setNodes, screenToFlowPosition } = useReactFlow()

  const defaultPin = defaultData.connectorFamilies[0]?.connectorFamilyPins?.[0]?.id || ''

  const defaultDataForNodeType: { [nodeType: string]: object } = useMemo(() => ({
    NoopTestStepType: { name: 'Noop step', steppable: { result: true } },
    LinkTestStepType: { name: 'Link step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    WireTestStepType: { name: 'Wire step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    WireFourWireTestStepType: { name: 'Wire FourWire step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    ResistanceTestStepType: { name: 'Resistance step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    ResistanceFourWireTestStepType: { name: 'Resistance Four Wire step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    CapacitanceTestStepType: { name: 'Capacitance step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    DiodeTestStepType: { name: 'Diode step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    TwistedPairTestStepType: { name: 'Twisted Pair step', steppable: { connectorFamilyPinAId: defaultPin, connectorFamilyPinBId: defaultPin } },
    HipotTestStepType: { name: 'Hipot step', steppable: { voltage: 500, connectorFamilyPinId: defaultPin } },
    RcdTestStepType: { name: 'RCD step', steppable: { breakerCount: 1, phaseAngle: 0, tripCurrent: 15 } },
    LoadTestStepType: { name: 'Load step', steppable: {} },
  }), [defaultPin])

  const handleNodeDrop = useCallback(
    (nodeType: string, screenPosition: XYPosition) => {
      const flow = document.querySelector('.react-flow')
      const flowRect = flow?.getBoundingClientRect()
      const isInFlow
        = flowRect &&
        screenPosition.x >= flowRect.left &&
        screenPosition.x <= flowRect.right &&
        screenPosition.y >= flowRect.top &&
        screenPosition.y <= flowRect.bottom

      if (isInFlow) {
        const position = screenToFlowPosition(screenPosition)

        const newNode = {
          id: getId(),
          type: nodeType,
          position,
          data: { label: `${nodeType} node`, itemTypeId, ...defaultData, ...defaultDataForNodeType[nodeType] },
        }

        setNodes(nds => nds.concat(newNode))
      }
    },
    [setNodes, screenToFlowPosition, itemTypeId, defaultData, defaultDataForNodeType],
  )

  return (
    <aside className="flex flex-col gap-6 w-full max-w-62.5">
      <div className="flex flex-col gap-3">
        <DraggableNode className="border-zinc-500 bg-zinc-500/10" nodeType="NoopTestStepType" onDrop={ handleNodeDrop }>Noop</DraggableNode>
        { defaultPin !== '' && <DraggableNode className="border-pink-500 bg-pink-500/10" nodeType="LinkTestStepType" onDrop={ handleNodeDrop }>Link</DraggableNode> }
        { defaultPin !== '' && <DraggableNode className="border-purple-500 bg-purple-500/10" nodeType="WireTestStepType" onDrop={ handleNodeDrop }>Wire</DraggableNode> }
        { defaultPin !== '' && <DraggableNode className="border-purple-500 bg-purple-500/10" nodeType="WireFourWireTestStepType" onDrop={ handleNodeDrop }>Wire Four Wire</DraggableNode> }
        { /* { defaultPin !== '' && <DraggableNode className="border-amber-500 bg-amber-500/10" nodeType="ResistanceTestStepType" onDrop={ handleNodeDrop }>Resistance</DraggableNode> } */ }
        { /* { defaultPin !== '' && <DraggableNode className="border-amber-500 bg-amber-500/10" nodeType="ResistanceFourWireTestStepType" onDrop={ handleNodeDrop }>Resistance Four Wire</DraggableNode> } */ }
        { /* { defaultPin !== '' && <DraggableNode className="border-green-500 bg-green-500/10" nodeType="CapacitanceTestStepType" onDrop={ handleNodeDrop }>Capacitance</DraggableNode> } */ }
        { /* { defaultPin !== '' && <DraggableNode className="border-blue-500 bg-blue-500/10" nodeType="DiodeTestStepType" onDrop={ handleNodeDrop }>Diode</DraggableNode> } */ }
        { /* { defaultPin !== '' && <DraggableNode className="border-orange-500 bg-orange-500/10" nodeType="TwistedPairTestStepType" onDrop={ handleNodeDrop }>Twisted Pair</DraggableNode> } */ }
        { defaultPin !== '' && <DraggableNode className="border-red-500 bg-red-500/10" nodeType="HipotTestStepType" onDrop={ handleNodeDrop }>Hipot</DraggableNode> }
        <DraggableNode className="border-cyan-500 bg-cyan-500/10" nodeType="RcdTestStepType" onDrop={ handleNodeDrop }>RCD</DraggableNode>
        { /* <DraggableNode className="border-violet-500 bg-violet-500/10" nodeType="LoadTestStepType" onDrop={ handleNodeDrop }>Load</DraggableNode> */ }
      </div>
      <div className="flex flex-col gap-3">
        <Text size="xl">Help</Text>
        <Text>Drag a box above onto the designer to the left to add that step to your test</Text>
        <Text>If you can't see the step you're looking for, you might need to add a connector to this type first</Text>
        <Text>Define the order of your steps by linking the bottom handle of any step into the top handle of any other step. Your tester will run steps from the top down</Text>
        <Text>To remove a step, click it and then press the backspace key on your keyboard. Similarly, to remove a link, click then link and then backspace to delete</Text>
      </div>
    </aside>
  )
}
